import React, { useState, useEffect, useRef, useCallback, memo } from "react";
import { VideoProvider, useVideo } from "./contexts/VideoContext"; // Оставьте, если контекст внешний, или перенесите его код сюда
import "./AIZerefullscreen.css";
import { ReactComponent as RecordIcon } from "./icons/record.svg";
import { ReactComponent as StopIcon } from "./icons/stop.svg";
import { fetchWeatherData } from "./services/weatherService";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBolt,
  faCloudRain,
  faCloudSun,
  faCloud,
  faSun,
  faQuestion,
  faTint,
  faWind,
  faSnowflake,
  faS,
  faSmog,
} from "@fortawesome/free-solid-svg-icons";

// === КОНСТАНТЫ И КОНФИГУРАЦИЯ ===
const API_BASE_URL = "http://localhost:5000"; // Вынесли для удобства смены

const VIDEO_SOURCES = {
  idle: "./video/robotidle.mp4",
  listen: "./video/robotlisten.mp4",
  think: "./video/robotthinking.mp4",
  say: "./video/robotsaying.mp4",
};

const LANGUAGES = [
  { code: "kk", label: "Қазақша" },
  { code: "ru", label: "Русский" },
  { code: "en", label: "English" },
];

// Вспомогательная функция для иконок погоды (вынесена из компонента)
const getWeatherIcon = weatherCode => {
  if (weatherCode >= 200 && weatherCode < 300) return faBolt;
  if (weatherCode >= 300 && weatherCode < 500) return faCloudRain;
  if (weatherCode >= 500 && weatherCode < 600) return faSnowflake;
  if (weatherCode >= 600 && weatherCode < 700) return faS;
  if (weatherCode >= 700 && weatherCode < 800) return faSmog;
  if (weatherCode === 800) return faSun;
  if (weatherCode === 801) return faCloudSun;
  if (weatherCode > 801 && weatherCode < 805) return faCloud;
  return faQuestion;
};

// === КОМПОНЕНТЫ ===

const WeatherInfo = memo(() => {
  const [weather, setWeather] = useState(null);

  useEffect(() => {
    let isMounted = true;
    const loadWeather = async () => {
      try {
        const data = await fetchWeatherData();
        if (isMounted) setWeather(data);
      } catch (error) {
        console.error("Failed to fetch weather:", error);
      }
    };

    loadWeather();
    const interval = setInterval(loadWeather, 30 * 60 * 1000);
    return () => {
      isMounted = false;
      clearInterval(interval);
    };
  }, []);

  if (!weather || !weather.hourly || !weather.hourly[0]) {
    return (
      <div className="weather-info">
        <div className="weather-icon">
          <FontAwesomeIcon icon={faQuestion} />
        </div>
        <div className="weather-temp">--°C</div>
        <div className="weather-details">
          <div className="weather-humidity">
            <FontAwesomeIcon icon={faTint} /> --%
          </div>
          <div className="weather-wind">
            <FontAwesomeIcon icon={faWind} /> -- км/ч
          </div>
        </div>
      </div>
    );
  }

  const current = weather.hourly[0];
  const icon = getWeatherIcon(current.weather[0].id);

  return (
    <div className="weather-info">
      <div className="weather-icon">
        <FontAwesomeIcon icon={icon} />
      </div>
      <div className="weather-temp">{Math.round(current.temp)}°C</div>
      <div className="weather-details">
        <div className="weather-humidity">
          <FontAwesomeIcon icon={faTint} /> {current.humidity}%
        </div>
        <div className="weather-wind">
          <FontAwesomeIcon icon={faWind} />{" "}
          {Math.round(current.wind_speed * 3.6)} км/ч
        </div>
      </div>
    </div>
  );
});

// Оптимизированный видео-фон. Теперь рендерится внутри React-дерева.
// Убедитесь, что в CSS для .assistantVideo заданы position: fixed/absolute и z-index,
// чтобы видео было фоном.
const VideoBackground = memo(() => {
  const { currentVideo } = useVideo();
  const videoRefs = useRef({});

  useEffect(() => {
    // Управление воспроизведением при смене currentVideo
    Object.entries(videoRefs.current).forEach(([key, videoEl]) => {
      if (!videoEl) return;

      if (key === currentVideo) {
        // Активное видео
        /* videoEl.currentTime = 0; // Раскомментировать, если нужно начинать сначала при каждом переключении */
        const playPromise = videoEl.play();
        if (playPromise !== undefined) {
          playPromise.catch(e => {
            if (
              !e.message.includes("interrupted") &&
              !e.message.includes("AbortError")
            ) {
              // console.warn("Auto-play prevented:", e); // Подавляем некритичные ошибки
            }
          });
        }
      } else {
        // Неактивные видео ставим на паузу для экономии ресурсов
        videoEl.pause();
      }
    });
  }, [currentVideo]);

  return (
    <div className="video-background-container">
      {Object.entries(VIDEO_SOURCES).map(([key, src]) => (
        <video
          key={key}
          ref={el => (videoRefs.current[key] = el)}
          src={src}
          className="assistantVideo"
          // Используем style для переключения видимости вместо условного рендеринга,
          // чтобы избежать мерцания при загрузке видео
          style={{ display: currentVideo === key ? "block" : "none" }}
          preload="auto"
          loop
          muted
          playsInline
        />
      ))}
    </div>
  );
});

const TimeDisplay = memo(() => {
  const [currentTime, setCurrentTime] = useState(new Date());

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  return (
    <div className="current-time">
      <div id="time">
        {currentTime.toLocaleTimeString("ru-RU", {
          hour: "2-digit",
          minute: "2-digit",
          second: "2-digit",
        })}
      </div>
      <div id="date">
        {currentTime.toLocaleDateString("ru-RU", {
          day: "numeric",
          month: "numeric",
          year: "numeric",
        })}
      </div>
    </div>
  );
});

const RecordButton = memo(({ setVideoState, onRecordingComplete }) => {
  const [isRecording, setIsRecording] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const recorderRef = useRef(null);
  // Используем ref для блокировки быстрых нажатий, не вызывая ререндер
  const isTogglingRef = useRef(false);

  useEffect(() => {
    let isMounted = true;
    const init = async () => {
      try {
        const Recorder = (await import("./utils/recorder")).default;
        const recorderInstance = new Recorder();
        const success = await recorderInstance.init();

        if (isMounted) {
          if (success) {
            recorderRef.current = recorderInstance;
            setIsInitialized(true);
          } else {
            console.error("Recorder init failed");
          }
        }
      } catch (e) {
        console.error("Failed to load recorder:", e);
      }
    };
    init();
    return () => {
      isMounted = false;
      if (recorderRef.current?.cleanup) recorderRef.current.cleanup();
    };
  }, []);

  const handleClick = useCallback(async () => {
    if (!isInitialized || isTogglingRef.current) return;

    isTogglingRef.current = true;

    try {
      if (isRecording) {
        await recorderRef.current.stop();
        // setIsRecording(false) вызовется в callback'е ниже
      } else {
        // Начинаем запись
        recorderRef.current.setOnRecordingStop(audioBlob => {
          onRecordingComplete(audioBlob);
          setIsRecording(false);
        });

        await recorderRef.current.start();
        setIsRecording(true);
        setVideoState("listen");
      }
    } catch (error) {
      console.error("Recording action error:", error);
      setIsRecording(false);
      setVideoState("idle");
    } finally {
      // Разблокируем кнопку через небольшой таймаут для стабильности
      setTimeout(() => {
        isTogglingRef.current = false;
      }, 500);
    }
  }, [isInitialized, isRecording, setVideoState, onRecordingComplete]);

  return (
    <button
      className={`record-button ${isRecording ? "recording" : ""}`}
      onClick={handleClick}
      disabled={!isInitialized}>
      {isRecording ? (
        <StopIcon width="40" height="40" />
      ) : (
        <RecordIcon width="40" height="40" />
      )}
    </button>
  );
});

const LanguageButtons = memo(({ selectedLanguage, onLanguageChange }) => (
  <div className="language-buttons">
    {LANGUAGES.map(({ code, label }) => (
      <button
        key={code}
        className={`language-button ${
          selectedLanguage === code ? "active" : ""
        }`}
        onClick={() => onLanguageChange(code)}>
        {label}
      </button>
    ))}
  </div>
));

// Простые компоненты можно оставить как есть или тоже мемоизировать
const Footer = memo(() => (
  <div className="footer">
    <div className="author">by daur9613</div>
  </div>
));
const Header = memo(() => (
  <>
    <div className="kazitu">AITIMOV</div>
    <div className="ainame">AI-Zere</div>
  </>
));

const BannerText = memo(() => {
  const [text, setText] = useState("🔥 Добро пожаловать! Загружаем текст...");

  useEffect(() => {
    let isMounted = true;
    fetch("/config.json")
      .then(res => (res.ok ? res.json() : { bannerText: text }))
      .then(data => {
        if (isMounted && data.bannerText) setText(data.bannerText);
      })
      .catch(err =>
        console.warn("Config load failed, using default banner", err),
      );

    return () => {
      isMounted = false;
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="banner-info">
      <div className="marquee-container" data-text={text}>
        <div id="bannertext">{text}</div>
      </div>
    </div>
  );
});

// === MAIN APP CONTENT ===

function AppContent() {
  const [selectedLanguage, setSelectedLanguage] = useState("ru");
  const { setVideoState } = useVideo();
  const audioRef = useRef(new Audio()); // Один экземпляр Audio для всего приложения

  useEffect(() => {
    // Сохраняем старые стили, чтобы восстановить их при уходе
    const originalStyle = {
      overflow: document.body.style.overflow,
      position: document.body.style.position,
      width: document.body.style.width,
      height: document.body.style.height,
      touchAction: document.body.style.touchAction,
      backgroundColor: document.body.style.backgroundColor,
    };

    // Применяем стили для полноэкранного режима
    document.body.style.overflow = "hidden";
    document.body.style.position = "fixed";
    document.body.style.width = "100vw";
    document.body.style.height = "100vh";
    document.body.style.touchAction = "none";
    document.body.style.backgroundColor = "#000";

    // Функция очистки, которая запустится при уходе со страницы
    return () => {
      // Восстанавливаем старые стили
      document.body.style.overflow = originalStyle.overflow;
      document.body.style.position = originalStyle.position;
      document.body.style.width = originalStyle.width;
      document.body.style.height = originalStyle.height;
      document.body.style.touchAction = originalStyle.touchAction;
      document.body.style.backgroundColor = originalStyle.backgroundColor;
    };
  }, []); // Пустой массив зависимостей означает, что хук запустится 1 раз при монтировании

  // Очистка аудио при размонтировании
  useEffect(() => {
    const audio = audioRef.current;
    return () => {
      audio.pause();
      audio.src = "";
    };
  }, []);

  const playAudioResponse = useCallback(
    audioUrl => {
      const audio = audioRef.current;
      // Сбрасываем предыдущие обработчики, чтобы не накладывались
      audio.onended = null;
      audio.onerror = null;

      audio.src = `${API_BASE_URL}${audioUrl}`;
      audio.onended = () => setVideoState("idle");
      audio.onerror = e => {
        console.error("Audio playback error:", e);
        setVideoState("idle");
      };

      setVideoState("say");
      audio.play().catch(e => {
        console.error("Audio play failed (autoplay policy?):", e);
        setVideoState("idle");
      });
    },
    [setVideoState],
  );

  const handleRecordingComplete = useCallback(
    audioBlob => {
      setVideoState("think");
      const formData = new FormData();
      formData.append("audio", audioBlob, "recording.wav");
      formData.append("language", selectedLanguage);

      fetch(`${API_BASE_URL}/transcribe`, { method: "POST", body: formData })
        .then(res => {
          if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);
          return res.json();
        })
        .then(data => {
          if (data.audio_url) {
            playAudioResponse(data.audio_url);
          } else {
            // Если сервер ответил без audio_url, возвращаемся в idle
            setVideoState("idle");
          }
        })
        .catch(error => {
          console.error("Transcription error:", error);
          setVideoState("idle");
        });
    },
    [selectedLanguage, setVideoState, playAudioResponse],
  );

  return (
    <div className="app">
      <div className="top-info">
        <WeatherInfo />
        <TimeDisplay />
      </div>
      <Header />
      <BannerText />
      <VideoBackground />

      <div className="controls">
        <LanguageButtons
          selectedLanguage={selectedLanguage}
          onLanguageChange={setSelectedLanguage}
        />
        <RecordButton
          setVideoState={setVideoState}
          onRecordingComplete={handleRecordingComplete}
        />
      </div>
      <Footer />
    </div>
  );
}

// Основной компонент-обертка
export default function AIZerefullscreen() {
  return (
    <VideoProvider>
      <AppContent />
    </VideoProvider>
  );
}
